import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAsyncValue, useNavigate, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../../offline/useRevalidateOnSync';
import { useTranslation } from 'react-i18next';

// Styles
import { Container } from './styles';
import { ContainerButton } from '../../components/MainStyledComponents';

//Components
import { InputRadioTagTable } from '../../../../../components/display/tables/InputRadioTagTable';
import { MainButton } from '../../../../../components/inputs/buttons/MainButton';

// Types
import { dataTypes } from '../../../../../components/display/tables/InputRadioTagTable/types';
import {
  tableIds,
  takeBase,
} from '../../../../../components/display/tables/types';
import { firstPageTake } from '../../../../../utils/cursor-take.helper';
import { OrderType } from '../../../../../types/orders';

// GQL
import { client } from '../../../../../gql/client';
import { GET_GENERIC_ORDERS } from '../../../../../gql/queries/genericOrders';
import { GET_ORDER_BY_ID } from '../../../../../gql/queries/orders/orders';

// Redux
import { useSelector } from 'react-redux';
import { getCursor } from '../../../../../state/selectors/ui/tables';
import isEqual from 'lodash.isequal';
import { roidOTSelected } from '../../../../../state/actions/ui/roidOTSelectedGeneralView';
import { setCursor } from '../../../../../state/actions/ui/cursorTables';
import { store } from '../../../../../state/configureStore';

// Hooks
import { usePollingRevalidation } from '../../../../../hooks/usePollingRevalidation';
import { withNavigationFeedback } from '../../../../../utils/navigationFeedback';

export const WorkOrdersPage = () => {
  const { t } = useTranslation(['workOrders', 'pages']);
  const data = useAsyncValue() as dataTypes;
  const revalidator = useRevalidator();
  useRevalidateOnSync();
  const dispatch = useDispatch();
  const [selectedRowId, setSelectedRowId] = useState<string>('');
  const navigate = useNavigate();
  const handleSeeOrder = useCallback(() =>
    withNavigationFeedback(async () => {
    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: selectedRowId,
      },
    });
    dispatch(
      roidOTSelected(
        OrderType.Generic,
        Array.isArray(order.roid) ? order.roid.join('.') : order.roid,
        selectedRowId
      )
    );
    navigate(`/work-orders/${selectedRowId}`);
  }), [selectedRowId]);

  const dataSelector = useSelector(
    getCursor(tableIds.WORKORDER_GENERAL_VIEW),
    isEqual
  );

  const onReFetchData = useCallback(async () => {
    // Always read from store.getState() — avoids stale closure when called from onClearValue
    const currentCursor = getCursor(tableIds.WORKORDER_GENERAL_VIEW)(store.getState());
    // Tarea #11: respetar el take guardado en cursor (page-size selector).
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    await client.query({
      query: GET_GENERIC_ORDERS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          concessionId: currentCursor.concessionId,
          teamIds: currentCursor.teamIds ?? [],
          sortField: currentCursor.sortField,
          orderBy: currentCursor.orderBy ?? 'desc',
          toDate: currentCursor.toDate,
          fromDate: currentCursor.fromDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          predefinedOrderId: currentCursor.predefinedOrderId,
          capitolId: currentCursor.capitolId,
          subCapitolId: currentCursor.subCapitolId,
          states: currentCursor.states,
          userIds: currentCursor.userIds,
          pkInitKm: currentCursor.pkInitKm,
          pkInitMeter: currentCursor.pkInitMeter,
          pkEndKm: currentCursor.pkEndKm,
          pkEndMeter: currentCursor.pkEndMeter,
          track: currentCursor.track,
          onlyFlagged: currentCursor.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });
    // sortField y orderBy se omiten del setCursorData: el reducer hace MERGE y los
    // preserva del cursor actual (evita pisarlos con un hardcode).
    const setCursorData = {
      take: effectiveTake,
      cursor: '',
      concessionId: currentCursor.concessionId,
      teamIds: currentCursor.teamIds ?? [],
      toDate: currentCursor.toDate,
      fromDate: currentCursor.fromDate,
      indexType: currentCursor.indexType,
      indexExpedition: currentCursor.indexExpedition,
      classification: currentCursor.classification,
      predefinedOrderId: currentCursor.predefinedOrderId,
      capitolId: currentCursor.capitolId,
      subCapitolId: currentCursor.subCapitolId,
      states: currentCursor.states,
      userIds: currentCursor.userIds,
      pkInitKm: currentCursor.pkInitKm,
      pkInitMeter: currentCursor.pkInitMeter,
      pkEndKm: currentCursor.pkEndKm,
      pkEndMeter: currentCursor.pkEndMeter,
      track: currentCursor.track,
      onlyFlagged: currentCursor.onlyFlagged,
    };

    dispatch(setCursor(tableIds.WORKORDER_GENERAL_VIEW, setCursorData));
    revalidator.revalidate();
  }, []);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, []);

  // Polling: refresca datos de pág 1 en cache (network-only) sin cambiar posición del usuario
  const onPollingRefresh = useCallback(async () => {
    const currentCursor = getCursor(tableIds.WORKORDER_GENERAL_VIEW)(store.getState());
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    await client.query({
      query: GET_GENERIC_ORDERS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          concessionId: currentCursor.concessionId,
          teamIds: currentCursor.teamIds ?? [],
          sortField: currentCursor.sortField,
          orderBy: currentCursor.orderBy ?? 'desc',
          toDate: currentCursor.toDate,
          fromDate: currentCursor.fromDate,
          indexType: currentCursor.indexType,
          indexExpedition: currentCursor.indexExpedition,
          classification: currentCursor.classification,
          predefinedOrderId: currentCursor.predefinedOrderId,
          capitolId: currentCursor.capitolId,
          subCapitolId: currentCursor.subCapitolId,
          states: currentCursor.states,
          userIds: currentCursor.userIds,
          pkInitKm: currentCursor.pkInitKm,
          pkInitMeter: currentCursor.pkInitMeter,
          pkEndKm: currentCursor.pkEndKm,
          pkEndMeter: currentCursor.pkEndMeter,
          track: currentCursor.track,
          onlyFlagged: currentCursor.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });
    revalidator.revalidate();
  }, []);
  usePollingRevalidation(onPollingRefresh);

  return (
    <Container>
      <InputRadioTagTable
        titleText={t('pages:workOrders.filterBy')}
        data={data}
        setRowState={setSelectedRowId}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        tableId={tableIds.WORKORDER_GENERAL_VIEW}
        // La tabla es routing-agnóstica (2026-07-09): el doble-click a
        // /work-orders/:id lo aporta el consumidor (antes era el default
        // interno de la tabla vía useNavigate; se retiró para poder usarla
        // en modales fuera del <Router>).
        onRowDoubleTap={(row) => {
          if (row.original.id) navigate(`/work-orders/${row.original.id}`);
        }}
        // Banderitas (spec 2026-10-22 § 8): columna y filtro «Solo marcadas».
        showFlagColumn
        showOnlyFlaggedFilter
      />
      <ContainerButton>
        <MainButton
          text={t('pages:workOrders.view')}
          disabled={selectedRowId.length === 0}
          onClick={handleSeeOrder}
        />
      </ContainerButton>
    </Container>
  );
};
